import React, { lazy, Suspense, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import "../src/styles/App.scss";
import "@emran-alhaddad/saudi-riyal-font/index.css";
import TopLoader from "./bibilio/loader/TopLoader";
import ProtectedRoute from "./apis/utils/ProtectedRoutes";
import ConditionalRenderComponents from "./components/ConditionalRenderComponents";
import Rating from "./components/moyassar/Rating";
import PrintBankTransferModal from "./components/bank-transfer/PrintBankTransferReceipt";
import TikTokPixel from "tiktok-pixel";

const Home = lazy(() => import("./domains/pages/Home"));
const AboutUs = lazy(() => import("./domains/pages/AboutUs"));
const DonorProfile = lazy(() => import("./domains/profile/donor/DonorProfile"));
const BeneficiaryProfile = lazy(() => import("./domains/profile/beneficiary/BeneficiaryProfile"));
const LayoutDetailsProgram = lazy(() => import("./domains/pages/programsLayout"));
const MoyasarResult = lazy(() => import("./components/moyassar/MoyassarResult"));
const PartnersList = lazy(() => import("./domains/pages/partners/PartnersList"));
const AddPartnership = lazy(() => import("./domains/pages/partners/addPartnership"));
const NewsSection = lazy(() => import("./domains/pages/news/NewsList"));
const NewsDetails = lazy(() => import("./domains/pages/news/NewsDetails"));
const ProjectDetails = lazy(() => import("./domains/pages/projects/ProjectDetails"));
const ProjectsPage = lazy(() => import("./domains/pages/projects/Projects"));
const PhotoAlbum = lazy(() => import("./domains/pages/photosVideos/Photos"));
const VideoAlbum = lazy(() => import("./domains/pages/photosVideos/Videos"));
const AddActiveBeneficiary = lazy(() => import("./domains/pages/add_beneficiary/AddActiveBeneficiary"));
const ReportsPage = lazy(() => import("./domains/pages/ReportsPage"));
const CertificateAward = lazy(() => import("./domains/pages/CertifactesAwards"));
const PermitsAssociation = lazy(() => import("./domains/pages/PermitsAssociation"));
const SuggestionsComplaints = lazy(() => import("./domains/pages/contact-us/SuggestionsComplains"));
const ContactUs = lazy(() => import("./domains/pages/contact-us/ContactUs"));
const OrganizationalStructure = lazy(() => import("./domains/pages/orgazitional-structure/OrganizitionalStructurePage"));
const DonationMethods = lazy(() => import("./domains/pages/DonationMethods"));
const JobsOpportunities = lazy(() => import("./domains/pages/jobs/JobsOpportunities"));
const MoreNumbers = lazy(() => import("./domains/pages/MoreNumbersPage"));
const CoverPageWrapper = lazy(() => import("./domains/pages/cover/CoverPageWrapper"));
const GiftWrapper = lazy(() => import("./domains/pages/gift/GiftWrapper"));
const CommitteesPage = lazy(() => import("./domains/pages/committees/CommitteesPage"));
const InvestmentPage = lazy(() => import("./domains/pages/investments/InvestmentsPage"));
const OurBranches = lazy(() => import("./domains/pages/branches/ourBranches"));
const FoundingMembers = lazy(() => import("./domains/pages/founders/foundingMembersPage"));
const DirectorsBoard = lazy(() => import("./domains/pages/directors_board/DirectorsBoardPage"));
const GeneralAssemblyPage = lazy(() => import("./domains/pages/general_assembly/GeneralAssemblyPage"));
const CeoPage = lazy(() => import("./domains/pages/ceo/ceoPage"));
const HigherManagment = lazy(() => import("./domains/pages/higher-managment/higherManagmentPage"));
const PodcastPage = lazy(() => import("./domains/pages/podcast/PodcastPage"));
const UmrahPage = lazy(() => import("./domains/pages/umrah/UmrahPage"));
const OriginPage = lazy(() => import("./domains/pages/origin/OriginEstablishmentPage"));
const ZakatPage = lazy(() => import("./domains/pages/zakatPage"));
const MealsWrapper = lazy(() => import("./domains/pages/ramadan_meals/MealsWrapper"));
const AdahiTrackingPage = lazy(() => import("./domains/pages/adahi-tracking/AdahiTrackingPage"));
const ScanBeneficiaryPage = lazy(() => import("./domains/pages/initiative/ScanPage"));
const InvitationPage = lazy(() => import("./domains/pages/initiative/InvitationPage"));

// Create a ProfileRouter component that checks the role
const ProfileRouter = () => {
  const [cookies] = useCookies(["role"]);
  const role = cookies.role;
  if (role === "donor") {
    return <DonorProfile />;
  } else if (role === "beneficiary") {
    return <BeneficiaryProfile />;
  } else {
    return <Navigate to="/" replace />;
  }
};

function App() {
  const location = useLocation();
  const firedUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const initTikTokPixel = () => {
      TikTokPixel.init("D5B51SBC77U4D2G7VKU0", {
        debug: process.env.NODE_ENV !== "production",
      });
    };
    if (document.readyState === "complete") {
      initTikTokPixel();
      return;
    }
    window.addEventListener("load", initTikTokPixel);
    return () => window.removeEventListener("load", initTikTokPixel);
  }, []);

  useEffect(() => {
    const path = location.pathname;

    if (!firedUrlsRef.current.has(path)) {
      (window as any).snaptr("track", "PAGE_VIEW");
      firedUrlsRef.current.add(path);
    }
  }, [location.pathname]);

  return (
    <div className="ghaith--app-container">
      <TopLoader />
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/profile" element={<ProfileRouter />} />
        </Route>
        <Route
          path="/program/:access_token"
          element={<LayoutDetailsProgram />}
        />{" "}
        <Route path="/korba/:access_token" element={<LayoutDetailsProgram />} />
        <Route
          path="/initiative/invitation/:token"
          element={<InvitationPage />}
        />
        <Route path="/donation" element={<MoyasarResult />} />
        <Route path="/partners" element={<PartnersList />} />
        <Route path="/add-partnership" element={<AddPartnership />} />
        <Route path="/news" element={<NewsSection type="news" />} />
        <Route path="/events" element={<NewsSection type="events" />} />
        <Route path="/media-eyes" element={<NewsSection type="media_eyes" />} />
        <Route
          path="/news/:access_token"
          element={<NewsDetails type="news" />}
        />{" "}
        <Route
          path="/event/:access_token"
          element={<NewsDetails type="event" />}
        />
        <Route
          path="/media-eyes/:access_token"
          element={<NewsDetails type="media_eyes" />}
        />
        <Route path="/project/:access_token" element={<ProjectDetails />} />
        <Route path="/general-projects" element={<ProjectsPage type="general" />} />
        <Route path="/seasonal-projects" element={<ProjectsPage type="seasonal" />} />
        <Route path="/our-photo" element={<PhotoAlbum />} />
        <Route path="/our-video" element={<VideoAlbum />} />
        <Route path="/beneficiary/add" element={<AddActiveBeneficiary />} />
        <Route
          path="/regulations"
          element={<ReportsPage type="regulations" />}
        />{" "}
        <Route
          path="/financial-statement"
          element={<ReportsPage type="financial_statement" />}
        />{" "}
        <Route
          path="/operational-plans"
          element={<ReportsPage type="operational_plans" />}
        />{" "}
        <Route
          path="/quality-development"
          element={<ReportsPage type="quality_development" />}
        />{" "}
        <Route
          path="/performance-reports"
          element={<ReportsPage type="performance_reports" />}
        />{" "}
        <Route
          path="/goals_programs"
          element={<ReportsPage type="goals_programs" />}
        />{" "}
        <Route path="/feedback" element={<ReportsPage type="feedback" />} />{" "}
        <Route path="/officials" element={<ReportsPage type="officials" />} />{" "}
        <Route
          path="/media-reports"
          element={<ReportsPage type="mediaReports" />}
        />{" "}
        <Route path="/our-certificates-awards" element={<CertificateAward />} />{" "}
        <Route path="/permits-association" element={<PermitsAssociation />} />{" "}
        <Route path="/donation-methods" element={<DonationMethods />} />
        <Route
          path="/suggestions-complaints"
          element={<SuggestionsComplaints />}
        />{" "}
        <Route
          path="/organizational-structure"
          element={<OrganizationalStructure />}
        />
        <Route path="/contact-us" element={<ContactUs />} />{" "}
        <Route path="/jobs" element={<JobsOpportunities />} />
        <Route path="/zakat_calculator" element={<ZakatPage />} />
        <Route path="/cover" element={<CoverPageWrapper />} />
        <Route path="/numbers" element={<MoreNumbers />} />
        <Route path="/gift" element={<GiftWrapper />} />
        <Route path="/ramadan_meals" element={<MealsWrapper />} />
        {/*}
        <Route path="/adahi-tracking" element={<AdahiTrackingPage />} />*/}
        <Route path="/initiative/scan" element={<ScanBeneficiaryPage />} />
        <Route path="/committees" element={<CommitteesPage />} />
        <Route path="/awqaf" element={<InvestmentPage />} />
        <Route path="/our-branches" element={<OurBranches />} />
        <Route path="/founding-members" element={<FoundingMembers />} />
        <Route path="/directors-board" element={<DirectorsBoard />} />{" "}
        <Route
          path="/general-assembly-members"
          element={<GeneralAssemblyPage />}
        />{" "}
        <Route path="/ceo" element={<CeoPage />} />
        <Route path="/higher-managment" element={<HigherManagment />} />
        <Route path="/podcasts" element={<PodcastPage />} />
        <Route path="/umrah" element={<UmrahPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route path="/origin-establishment" element={<OriginPage />} />
      </Routes>
      </Suspense>
      <ConditionalRenderComponents />
      <Rating isOpe={true} setIsOpen={(v) => console.log(v)} res_id={-1} />{" "}
      <PrintBankTransferModal />
    </div>
  );
}

export default App;
